import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Clock, ArrowRight, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';

const events = [
  {
    id: 'iwd2026',
    status: 'upcoming',
    badge: '다가오는 행사',
    badgeColor: 'bg-teal-500 text-white',
    title: 'International Women\'s Day 2026',
    subtitle: 'AI Ethics & Women\'s Leadership Forum',
    theme: 'Give to Gain',
    organizer: 'AI4HER APAC × 서울여자대학교',
    date: '2026년 3월 27일 (금)',
    time: '09:00 – 13:00',
    location: '서울여자대학교 50주년기념관 국제회의실',
    description: '학계, AI 산업, 바이오테크, 비즈니스, 공공기관 분야 여성 리더들이 모여 인간 중심의 윤리적·책임감 있는 AI에 대한 대화와 실천을 이끌어 냅니다. MoU 서명식 및 Ada Lovelace 시상식 포함.',
    highlights: [
      '기조연설 및 다분야 패널 토론',
      'APAC AI Advisory Group MoU 서명식',
      'AI4HER APAC Ada Lovelace IWD Awards 2026',
    ],
    href: '/ai4her-iwd2026',
    cardBg: 'bg-gradient-to-br from-navy-50 to-teal-50 border-teal-200',
    accentColor: 'text-teal-600',
  },
  {
    id: 'waitalk',
    status: 'past',
    badge: '지난 행사',
    badgeColor: 'bg-gray-500 text-white',
    title: 'AI4HER WaiTALK',
    subtitle: 'Women in AI APAC',
    theme: 'AI and Innovation for a Better Future',
    organizer: 'AI4HER APAC × 서울여자대학교',
    date: '2025년',
    time: '09:00 – 13:00',
    location: '서울여자대학교',
    description: 'APAC 지역 AI 분야 여성 전문가들이 모여 책임감 있는 AI, 의료 AI, 스마트팜, 디지털 민주주의 등 다양한 주제로 강연과 패널 토론을 진행했습니다.',
    highlights: [
      'Dr. Sue Keay (UNSW AI Institute) 기조연설',
      'AI & Innovation 패널 토론',
      '글로벌 AI 교육 협력 세션',
    ],
    href: '/ai4her-waitalk',
    cardBg: 'bg-white border-gray-200',
    accentColor: 'text-gray-600',
  },
];

const AI4HERHubPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="h-16" />
      <Breadcrumb />

      {/* Hero */}
      <section className="bg-gradient-to-r from-navy-900 via-navy-900 to-teal-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-pink-500/20 border border-pink-300/50 px-4 py-1.5 rounded-full text-sm font-medium mb-5">
            <span className="text-pink-200">행사 (AI4HER)</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-pink-300 to-purple-200 bg-clip-text text-transparent">AI4HER</span>
          </h1>
          <p className="text-lg text-purple-200 max-w-2xl mx-auto">
            인간-AI 협력 연구센터가 함께하는 AI4HER 행사 시리즈.<br />
            여성 리더십, AI 윤리, 글로벌 협력을 중심으로 APAC 전역과 연결합니다.
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">

        {/* Upcoming */}
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-3 h-3 rounded-full bg-teal-500 animate-pulse inline-block" />
            <h2 className="text-2xl font-bold text-gray-900">다가오는 행사</h2>
          </div>
          {events.filter(e => e.status === 'upcoming').map(event => (
            <div key={event.id} className={`rounded-2xl border-2 ${event.cardBg} p-8 shadow-md`}>
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${event.badgeColor}`}>{event.badge}</span>
                    <span className="text-xs text-teal-600 font-semibold border border-teal-200 px-3 py-1 rounded-full">Theme: "{event.theme}"</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{event.title}</h3>
                  <p className={`text-lg font-medium mb-4 ${event.accentColor}`}>{event.subtitle}</p>

                  <div className="grid sm:grid-cols-2 gap-3 mb-5 text-sm text-gray-600">
                    <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-teal-400" />{event.date}</div>
                    <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-teal-400" />{event.time}</div>
                    <div className="flex items-center gap-2 sm:col-span-2"><MapPin className="h-4 w-4 text-teal-400" />{event.location}</div>
                  </div>

                  <p className="text-gray-600 text-sm leading-relaxed mb-5">{event.description}</p>

                  <ul className="space-y-2 mb-6">
                    {event.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-teal-500 flex-shrink-0 mt-0.5" />
                        {h}
                      </li>
                    ))}
                  </ul>

                  <Link to={event.href} className="inline-flex items-center px-6 py-3 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors">
                    프로그램 자세히 보기 <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>

                {/* 날짜 카운트다운 강조 */}
                <div className="flex-shrink-0 bg-teal-500 text-white rounded-xl p-6 text-center min-w-[140px]">
                  <p className="text-xs font-medium text-teal-200 mb-1">D-DAY</p>
                  <p className="text-5xl font-bold">7</p>
                  <p className="text-xs text-teal-200 mt-1">2026. 3. 27</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Past */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-3 h-3 rounded-full bg-gray-400 inline-block" />
            <h2 className="text-2xl font-bold text-gray-900">지난 행사</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {events.filter(e => e.status === 'past').map(event => (
              <div key={event.id} className={`rounded-xl border ${event.cardBg} p-6 shadow-sm`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${event.badgeColor}`}>{event.badge}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{event.title}</h3>
                <p className={`text-sm font-medium mb-4 ${event.accentColor}`}>{event.subtitle}</p>

                <div className="space-y-1.5 mb-4 text-xs text-gray-500">
                  <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5" />{event.date}</div>
                  <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" />{event.location}</div>
                </div>

                <p className="text-gray-500 text-sm leading-relaxed mb-4">{event.description}</p>

                <Link to={event.href} className="inline-flex items-center text-sm text-gray-600 font-medium hover:text-teal-600 transition-colors">
                  행사 내용 보기 <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AI4HERHubPage;
